import { SetStateAction, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import {
  useUnifiedPayoutsPostAuthMutation,
  useUnifiedPayoutsPreAuthMutation,
} from 'hooks/mutations/FiatPayouts/useUnifiedPayouts'
import { usePayoutMutation } from 'hooks/mutations/usePayoutMutation'
import { useWithdrawMutation } from 'hooks/mutations/useWithdrawMutation'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse, BeneficiaryDetails, BeneficiaryType } from 'types/beneficiaries'
import { MappedErrorMessageState } from 'types/errors'
import { PayoutsV2PostAuthRequest } from 'types/payoutsV2'
import { FiatSendStep, FiatSendSteps } from 'types/walletActions'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { SourcePayWallet } from '../../Pay/types'
import { PAYOUT_VERSIONS } from '../constants'
import { FiatSendFormData, FiatSendFormKeys } from '../form/fiatSendFormSchema'
import { FiatSendFormProps } from '../types'
import { getLegacyPayoutRequest, getUnifiedPayoutRequestV2, logLegacyPayout } from '../utils'

interface HandleSentryLogProps {
  errorTitle: string
  error: BaseErrorResponse
  additionalData?: object
  errorCallout?: string
}

interface UsePayoutSubmitProps {
  fiatBeneficiary: BeneficiaryDetails
  selectedWallet: SourcePayWallet
  formValues: FiatSendFormData
  form: FiatSendFormProps
  setStep: (step: FiatSendStep) => void
  setRequestError: (value: SetStateAction<MappedErrorMessageState>) => void
}

export const usePayoutSubmit = ({
  fiatBeneficiary,
  selectedWallet,
  formValues,
  form,
  setStep,
  setRequestError,
}: UsePayoutSubmitProps) => {
  const { t } = useTranslation()
  const { twoFactorEnabled } = useTwoFAContext()
  const queryClient = useQueryClient()

  const [authRequest, setAuthRequest] = useState<PayoutsV2PostAuthRequest>(null)
  const [submitBeneficiary, setSubmitBeneficiary] = useState(false)

  const isLegacyPaymentMethod = !isNaN(Number(fiatBeneficiary?.reference))
  const isPayoutsV2Enabled =
    !isLegacyPaymentMethod && !!selectedWallet?.supportedTransferDestinations?.length

  const handleSentryLog = ({
    errorTitle,
    error,
    additionalData,
    errorCallout,
  }: HandleSentryLogProps) => {
    const sentryEventId = logSentryErrorAndGetEventId(errorTitle, error, additionalData)

    setRequestError({
      handledMessage: errorCallout,
      errorCode: error?.status,
      sentryTraceId: sentryEventId,
      show: true,
    })
  }

  // Legacy first and third party payouts
  const { mutate: withdrawMutation, isPending: isSubmittingLegacyWithdrawalRequest } =
    useWithdrawMutation()
  const { mutate: payBeneficiary, isPending: isSubmittingLegacyPayRequest } = usePayoutMutation()

  // Unified payouts
  const { mutate: submitPayoutRequest, isPending: isSubmittingUnifiedPayoutRequest } =
    useUnifiedPayoutsPreAuthMutation()
  const { mutate: submitPayoutAuthRequest, isPending: isSubmittingUnifiedPayoutAuthRequest } =
    useUnifiedPayoutsPostAuthMutation()

  const onSubmitPayoutHandler = () => {
    if (isPayoutsV2Enabled) {
      submitUnifiedPayout()
    } else {
      const isFirstParty = fiatBeneficiary?.beneficiaryType === BeneficiaryType.FIRST_PARTY

      logLegacyPayout(selectedWallet, fiatBeneficiary, isLegacyPaymentMethod)

      if (isFirstParty) {
        submitLegacyFirstPartyPayout()
        return
      }

      submitLegacyThirdPartyPayout()
    }
  }

  /* Legacy first and third party payouts */
  const submitLegacyFirstPartyPayout = async () => {
    const beneficiaryIdentifierKey = isLegacyPaymentMethod
      ? 'paymentMethodId'
      : 'beneficiaryReference'

    const withdrawRequest = {
      address: JSON.stringify({ [beneficiaryIdentifierKey]: fiatBeneficiary?.reference }),
      paymentReference: formValues?.paymentReference,
      amount: formValues.amount,
      authorization: {
        ...(twoFactorEnabled && { totp: formValues.twoFactorCode }),
        ...(!twoFactorEnabled && { password: formValues.twoFactorCode }),
      },
    }

    withdrawMutation(
      { walletId: selectedWallet?.legacyWalletId.toString(), requestData: withdrawRequest },
      {
        onSuccess: () => {
          setStep(FiatSendSteps.EOF)
          queryClient.invalidateQueries({
            queryKey: reactQueryKeys.walletTransactions({
              walletId: Number(selectedWallet?.walletId),
              offset: 0,
              max: 10,
            }),
          })
        },
        onError: (error: BaseErrorResponse) => {
          track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_ERROR, {
            category: AmplitudeEventCategory.MERCHANT_PORTAL,
            action: AmplitudeEventAction.ERROR,
            step: FiatSendSteps.CONFIRMATION,
            apiVersion: PAYOUT_VERSIONS.LEGACY_WITHDRAW,
            error,
          })

          if (error?.data?.errorList?.length) {
            error.data.errorList.forEach(err => {
              form.setError(err?.parameter as FiatSendFormKeys, { message: error?.message })
            })
          } else if (error?.status === 403) {
            form.setError('twoFactorCode', {
              message: twoFactorEnabled ? t('authCodeInvalid') : t('authPasswordInvalid'),
            })
          } else {
            handleSentryLog({
              errorTitle: 'Error from Confirmation - postWithdraw',
              error,
              additionalData: withdrawRequest,
              errorCallout: t('withdrawError'),
            })
          }
        },
      }
    )
  }

  const submitLegacyThirdPartyPayout = async () => {
    const payRequest = getLegacyPayoutRequest(formValues, fiatBeneficiary, twoFactorEnabled)

    payBeneficiary(
      { walletId: selectedWallet?.legacyWalletId?.toString(), payRequest },
      {
        onSuccess: () => {
          setStep(FiatSendSteps.EOF)
          queryClient.invalidateQueries({
            queryKey: reactQueryKeys.walletTransactions({
              walletId: Number(selectedWallet?.walletId),
              offset: 0,
              max: 10,
            }),
          })
        },
        onError: (error: BaseErrorResponse) => {
          track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_ERROR, {
            category: AmplitudeEventCategory.MERCHANT_PORTAL,
            action: AmplitudeEventAction.ERROR,
            step: FiatSendSteps.CONFIRMATION,
            apiVersion: PAYOUT_VERSIONS.LEGACY_PAY,
            error,
          })
          onErrorHandler('Error from payBeneficiary', error, payRequest)
        },
      }
    )
  }

  /* Unified Payouts */
  const submitUnifiedPayout = async () => {
    setRequestError(null)
    const payoutsPrerequest = getUnifiedPayoutRequestV2(formValues, selectedWallet, fiatBeneficiary)

    submitPayoutRequest(
      { verificationId: formValues?.beneficiaryVerificationId, request: payoutsPrerequest },
      {
        onError: (error: BaseErrorResponse) => {
          track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_ERROR, {
            category: AmplitudeEventCategory.MERCHANT_PORTAL,
            action: AmplitudeEventAction.ERROR,
            step: FiatSendSteps.CONFIRMATION,
            apiVersion: PAYOUT_VERSIONS.UNIFIED_PAYOUT_V2,
            error,
          })
          onErrorHandler('Error from submitPayoutRequest', error, payoutsPrerequest)
        },
      }
    )
  }

  const onErrorHandler = (title: string, error: BaseErrorResponse, additionalData?: object) => {
    if (error?.status === 428) {
      setAuthRequest({
        challenge: error?.data?.challenge?.value,
        tokenResponse: formValues?.twoFactorCode,
        mfaCallbackUrl: error?.data?.callbackUrl,
      })
      form?.setValue('challengeCode', error?.data?.challenge?.value)
      setSubmitBeneficiary(true)
    } else if (error?.data?.errorList) {
      error.data.errorList.forEach(err => {
        form.setError(err?.parameter as FiatSendFormKeys, { message: error?.message })
      })
    } else if (error?.status === 403) {
      form.setError('twoFactorCode', {
        message: twoFactorEnabled ? t('authCodeInvalid') : t('authPasswordInvalid'),
      })
    } else {
      let errorMessage = error?.data?.message || t('payoutError')
      const paymentReferenceError =
        error?.data?.details?.errors?.['beneficiary.remittanceInformation'] || []

      if (paymentReferenceError?.length) {
        errorMessage = `${t('formFields.paymentReference')}: ${paymentReferenceError.join('. ')}`
      }

      handleSentryLog({
        errorTitle: title,
        error,
        additionalData,
        errorCallout: errorMessage,
      })
    }
  }

  const onSubmitPostRequest = () => {
    const payoutRequest = getUnifiedPayoutRequestV2(formValues, selectedWallet, fiatBeneficiary)
    const payoutsPostrequest: PayoutsV2PostAuthRequest = {
      challenge: authRequest?.challenge,
      tokenResponse: authRequest?.tokenResponse,
      mfaCallbackUrl: authRequest?.mfaCallbackUrl,
    }

    submitPayoutAuthRequest(payoutsPostrequest, {
      onSuccess: () => {
        setStep(FiatSendSteps.EOF)
        queryClient.invalidateQueries({
          queryKey: reactQueryKeys.walletTransactions({
            walletId: Number(selectedWallet?.walletId),
            offset: 0,
            max: 10,
          }),
        })
      },
      onError: (error: BaseErrorResponse) => {
        track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_ERROR, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.ERROR,
          step: FiatSendSteps.CONFIRMATION,
          apiVersion: PAYOUT_VERSIONS.UNIFIED_PAYOUT_V2,
          error,
        })
        onErrorHandler('Error from submitPayoutAuthRequest', error, {
          payoutsPostrequest,
          payoutRequest,
        })
      },
      onSettled: () => {
        setSubmitBeneficiary(false)
      },
    })
  }

  useEffect(() => {
    submitBeneficiary && onSubmitPostRequest()
  }, [submitBeneficiary])

  return {
    onSubmitPayoutHandler,
    isLegacyPayoutLoading: isSubmittingLegacyPayRequest || isSubmittingLegacyWithdrawalRequest,
    isPayoutRequestLoading: isSubmittingUnifiedPayoutRequest,
    isPayoutAuthRequestLoading: isSubmittingUnifiedPayoutAuthRequest,
  }
}
